import { Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Barra de progresso reutilizável para uploads e downloads.
 *
 * Aceita bytes carregados/total via signals. Calcula % automaticamente.
 * Quando `total` é null/0, exibe barra indeterminada animada.
 *
 * @example
 *   <lc-upload-progress [loaded]="uploadProgress().loaded"
 *                       [total]="uploadProgress().total"
 *                       label="Enviando arquivo" />
 */
@Component({
    selector: 'lc-upload-progress',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './upload-progress.component.html',
    styleUrls: ['./upload-progress.component.scss'],
})
export class UploadProgressComponent {
    /** Bytes já carregados (enviados ou recebidos). */
    readonly loaded = input<number>(0);
    /** Bytes totais. Null = desconhecido → barra indeterminada. */
    readonly total = input<number | null>(null);
    /** Rótulo exibido acima da barra. */
    readonly label = input<string>('Enviando…');
    /** Se true, mostra "X.X MB / Y.Y MB" abaixo da barra. */
    readonly showBytes = input<boolean>(true);

    /** Porcentagem 0-100, ou null quando total é desconhecido. */
    readonly percent = computed<number | null>(() => {
        const t = this.total();
        const l = this.loaded();
        if (!t || t <= 0) return null;
        return Math.min(100, Math.round((l / t) * 100));
    });

    /** Largura da barra em % — usado no template. */
    readonly widthStyle = computed<string>(() => {
        const p = this.percent();
        if (p === null) return '0%';
        return `${p}%`;
    });

    /** Se a barra é indeterminada. */
    readonly indeterminate = computed<boolean>(() => this.percent() === null);

    /** Texto "X.X MB / Y.Y MB" formatado. */
    readonly bytesText = computed<string>(() => {
        const l = this.loaded();
        const t = this.total();
        const loadedStr = UploadProgressComponent.formatBytes(l);
        if (t === null || t === undefined || t <= 0) {
            return loadedStr;
        }
        const totalStr = UploadProgressComponent.formatBytes(t);
        return `${loadedStr} / ${totalStr}`;
    });

    /** Texto de percentual exibido ao lado do label (ex: "Enviando… 42%"). */
    readonly percentText = computed<string | null>(() => {
        const p = this.percent();
        return p === null ? null : `${p}%`;
    });

    /**
     * Formata um número de bytes em string legível (B / KB / MB / GB).
     * Usa base 1024 (binário).
     */
    static formatBytes(n: number): string {
        if (!Number.isFinite(n) || n < 0) return '0 B';
        if (n < 1024) return `${n} B`;
        const units = ['KB', 'MB', 'GB', 'TB'];
        let val = n / 1024;
        let i = 0;
        while (val >= 1024 && i < units.length - 1) {
            val /= 1024;
            i++;
        }
        return `${val.toFixed(val >= 100 ? 0 : val >= 10 ? 1 : 2)} ${units[i]}`;
    }
}
